import { chunk, flatten, isEmpty, map } from "lodash/fp";
import { DateTime } from "luxon";

import { get, patch, post } from "@app/common/api";
import type {
  ApiClip,
  ApiClipOperation,
  ApiClipType,
  ApiCreateClipOperation,
  ApiDeleteClipOperation,
  ApiHighlight,
  ApiUpdateClipOperation,
  CreateClipOperation,
  DeleteClipOperation,
  UpdateClipOperation,
} from "@app/common/types";
import { withQueryParamsUrl } from "@app/common/utils";

export interface ListClipsQuery {
  startDate: DateTime;
  endDate: DateTime;
  clipTypes?: ApiClipType[];
}

const listClipsByIds = async (ids: string[]) =>
  post("/clips/list", ids, { withCreds: true });

/**
 * Bulk endpoint for retrieving clips by ID
 * @param ids list of ids to fetch
 * @param batchSize maximum batch size to fetch. if larger than the number of ids, will fire off multiple parallel requests
 * */
export const listByIds = async (
  ids: string[],
  batchSize?: number
): Promise<ApiHighlight[]> => {
  if (batchSize) {
    const batches = chunk(batchSize, ids);
    const clips = await Promise.all(map(listClipsByIds, batches));
    return flatten(clips);
  }
  return listClipsByIds(ids);
};

export const list = async (
  query: ListClipsQuery
): Promise<{ data: ApiClip[] }> => {
  const url = withQueryParamsUrl("/clips", {
    start_date: query.startDate.toISO()!,
    end_date: query.endDate.toISO()!,
    clip_type: query.clipTypes,
  });

  return get(url, { withCreds: true });
};

export const patchAll = async (
  operations: ApiClipOperation[]
): Promise<void> => {
  if (isEmpty(operations)) return;
  return patch("/clips", operations, { withCreds: true });
};

const patchClip = async (operation: ApiClipOperation): Promise<void> => {
  return patchAll([operation]);
};

export const createClip = async (body: CreateClipOperation): Promise<void> => {
  const operation: ApiCreateClipOperation = {
    operation: "create",
    clipType: body.type,
    startTime: body.createTime,
    endTime: body.createTime,
    parentClipId: body.parentClipId,
    sessionId: body.sessionId,
    status: body.status,
    text: body.text,
  };

  return patchClip(operation);
};

export const updateClip = async (body: UpdateClipOperation): Promise<void> => {
  const operation: ApiUpdateClipOperation = {
    operation: "update",
    id: body.id,
    clipType: body.type,
    newClipType: body.newType,
    sessionId: body.sessionId,
    status: body.status,
    text: body.text,
  };

  return patchClip(operation);
};

export const deleteClip = async (body: DeleteClipOperation): Promise<void> => {
  const operation: ApiDeleteClipOperation = {
    operation: "delete",
    id: body.id,
    clipType: body.type,
    sessionId: body.sessionId,
  };

  return patchClip(operation);
};
